import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { Save, ExternalLink } from "lucide-react"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { ZurueckZurSuche } from "@/components/search/ZurueckZurSuche"
import { cn } from "@/lib/utils"

type Contact = {
  id: number
  first_name: string
  last_name: string
  email: string
  phone_mobile: string
  phone_home: string
  street: string
  postcode: string
  city: string
  kunden_slug: string
  salutation?: string
  is_contact_person?: boolean
  is_invoice_recipient?: boolean
}

/**
 * Kontakt-Detail + Edit. Nutzt /admin/contacts/api/{id} + /update direkt
 * (Session-Cookie). Port von admin/contacts.py-View.
 */
export default function ContactDetail() {
  const { id } = useParams<{ id: string }>()
  const [sp] = useSearchParams()
  const tenant = sp.get("tenant") || ""
  const qc = useQueryClient()
  const [form, setForm] = useState<Contact | null>(null)
  const [saveOk, setSaveOk] = useState(false)

  const { data, isLoading, error } = useQuery<Contact>({
    queryKey: ["contact", id, tenant],
    queryFn: async () => {
      const url = `/admin/contacts/api/${id}${tenant ? `?tenant=${encodeURIComponent(tenant)}` : ""}`
      const res = await fetch(url, { credentials: "include" })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || `HTTP ${res.status}`)
      }
      return res.json()
    },
    enabled: !!id,
  })

  useEffect(() => {
    if (data) setForm({ ...data })
  }, [data])

  const save = useMutation({
    mutationFn: async (c: Contact) => {
      const res = await fetch(`/api/v1/contacts/${c.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...c, tenant_slug: c.kunden_slug || tenant }),
        credentials: "include",
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact", id] })
      qc.invalidateQueries({ queryKey: ["contacts"] })
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 2500)
    },
  })

  if (isLoading) return <Loading label="Lädt Kontakt..." />
  if (error) return <div className="p-10"><ErrorBox error={error} /></div>
  if (!form) return null

  return (
    <div className="px-10 py-8 max-w-[900px]">
      <ZurueckZurSuche ersatz={{ to: "/contacts", label: "Zurück zur Kontakt-Liste" }} />

      <form
        onSubmit={(ev) => { ev.preventDefault(); save.mutate(form) }}
        className="rounded-lg border bg-card overflow-hidden shadow-xs"
      >
        <div className="px-6 py-4 border-b bg-secondary flex items-start justify-between gap-3">
          <div>
            <h1 className="text-[18px] font-bold" data-pii>
              {form.first_name} {form.last_name}
            </h1>
            <div className="text-[12px] text-muted-foreground mt-0.5">
              Kontakt #{form.id} · Kunde: {form.kunden_slug || "—"}
            </div>
            {form.is_invoice_recipient && (
              <div className="text-[12px] text-muted-foreground mt-1">
                Rolle in HERO: Rechnungsempfänger
              </div>
            )}
          </div>
          {form.kunden_slug && (
            <a
              href={`/admin/open-hero/contact/${form.id}?tenant=${form.kunden_slug}`}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" /> In HERO
            </a>
          )}
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Anrede" value={form.salutation || ""} onChange={(v) => setForm({ ...form, salutation: v })} />
          <FormField label="Vorname" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
          <FormField label="Nachname" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
          <FormField label="E-Mail" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
          <FormField label="Handy" value={form.phone_mobile} onChange={(v) => setForm({ ...form, phone_mobile: v })} />
          <FormField label="Festnetz" value={form.phone_home} onChange={(v) => setForm({ ...form, phone_home: v })} />
          <FormField label="Strasse" value={form.street} onChange={(v) => setForm({ ...form, street: v })} className="md:col-span-2" />
          <FormField label="PLZ" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} />
          <FormField label="Ort" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          {/* HERO-Rolle, kein Portal-Feld: gesetzt biegt HERO die Rechnungs-
              anschrift auf diesen Kontakt um — der Fall „Mieter ist
              Ansprechpartner, Rechnung an den Vermieter" (Ticket #2). */}
          <label className="md:col-span-2 flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.is_invoice_recipient}
              onChange={(e) => setForm({ ...form, is_invoice_recipient: e.target.checked })}
              className="mt-0.5 w-auto"
            />
            <span>
              <span className="text-[13px] font-medium">Rechnungsempfänger</span><br />
              <span className="text-[11px] text-muted-foreground">
                HERO schreibt die Rechnung an diesen Kontakt, auch wenn ein anderer
                Hauptkontakt ist. Die Anrede im Brief zieht nicht mit.
              </span>
            </span>
          </label>
        </div>

        <div className="px-6 py-4 border-t bg-secondary/50 flex items-center justify-between gap-3">
          <div className="text-[12px]">
            {save.isError && (
              <span className="text-destructive">
                Fehler: {save.error instanceof Error ? save.error.message : "unbekannt"}
              </span>
            )}
            {saveOk && <span className="text-success font-semibold">✓ Gespeichert</span>}
          </div>
          <button
            type="submit"
            disabled={save.isPending}
            className={cn(
              "inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
              save.isPending && "opacity-60 cursor-wait",
            )}
          >
            <Save className="w-3.5 h-3.5" />
            {save.isPending ? "Speichert..." : "Speichern (HERO + Cache)"}
          </button>
        </div>
      </form>

    </div>
  )
}

function FormField({
  label, value, onChange, type = "text", className,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  className?: string
}) {
  return (
    <div className={className}>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <input
        data-pii
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border bg-background px-3 py-1.5 text-[13.5px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"
      />
    </div>
  )
}
